import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { billPaymentAPI, accountsAPI, dashboardAPI } from '../services/api';
import { useNotification } from '../contexts/NotificationContext';
import { handleApiError } from '../utils/errorHandler';
import { useAuth } from '../contexts/AuthContext';
import { formatCurrency } from '../utils/currencyFormatter';

const BillPay = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('pay-bills');
  const [payees, setPayees] = useState([]);
  const [payments, setPayments] = useState([]);
  const [comprehensiveHistory, setComprehensiveHistory] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [categories, setCategories] = useState({});
  const [loading, setLoading] = useState(false);
  const [showAddPayee, setShowAddPayee] = useState(false);
  const [showSchedulePayment, setShowSchedulePayment] = useState(false);
  const [selectedPayee, setSelectedPayee] = useState(null);
  const [errors, setErrors] = useState({});
  const notification = useNotification();

  // Currency formatter for user's currency
  const formatCurrencyWithUserCurrency = (amount) => {
    return formatCurrency(amount, user?.currency_code || 'USD');
  };

  // Form states
  const [payeeForm, setPayeeForm] = useState({
    name: '',
    display_name: '',
    category: '',
    account_number: '',
    address: '',
    city: '',
    state: '',
    zip_code: '',
    phone: '',
    default_amount: '',
    autopay_enabled: false,
    autopay_day: '',
    autopay_amount: ''
  });

  const [paymentForm, setPaymentForm] = useState({
    payee_id: '',
    from_account_id: '',
    amount: '',
    scheduled_date: new Date().toISOString().split('T')[0],
    memo: '',
    is_recurring: false,
    recurring_frequency: '',
    recurring_end_date: ''
  });

  useEffect(() => {
    fetchInitialData();
  }, []);

  const fetchInitialData = async () => {
    setLoading(true);
    try {
      const [payeesRes, paymentsRes, comprehensiveRes, accountsRes, categoriesRes] = await Promise.all([
        billPaymentAPI.getPayees(),
        billPaymentAPI.getPaymentHistory(),
        dashboardAPI.getComprehensiveHistory({ type: 'all', limit: 50 }),
        accountsAPI.getAccounts(),
        billPaymentAPI.getCategories()
      ]);

      if (payeesRes.status === 'success') {
        setPayees(payeesRes.data.payees);
      }

      if (paymentsRes.status === 'success') {
        setPayments(paymentsRes.data.payments);
      }

      if (comprehensiveRes.status === 'success') {
        setComprehensiveHistory(comprehensiveRes.data.transactions);
      }

      if (accountsRes.status === 'success') {
        setAccounts(accountsRes.data.accounts);
      }

      if (categoriesRes.status === 'success') {
        setCategories(categoriesRes.data.categories);
      }
    } catch (error) {
      handleApiError(error, notification, 'Failed to load bill payment data');
    } finally {
      setLoading(false);
    }
  };

  const getCategoryIcon = (category) => {
    const icons = {
      utilities: '⚡',
      credit_cards: '💳',
      insurance: '🛡️',
      mortgage: '🏠',
      auto_loan: '🚗',
      phone: '📱',
      internet: '🌐',
      cable: '📺',
      streaming: '🎬',
      other: '📄'
    };
    return icons[category] || '📄';
  };

  const handlePayeeFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    setPayeeForm(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handlePaymentFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    setPaymentForm(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleAddPayee = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErrors({});

    try {
      const response = await billPaymentAPI.createPayee(payeeForm);
      if (response.status === 'success') {
        setPayees(prev => [...prev, response.data.payee]);
        setShowAddPayee(false);
        setPayeeForm({
          name: '',
          display_name: '',
          category: '',
          account_number: '',
          address: '',
          city: '',
          state: '',
          zip_code: '',
          phone: '',
          default_amount: '',
          autopay_enabled: false,
          autopay_day: '',
          autopay_amount: ''
        });
        notification.showSuccess('Payee added successfully!');
      }
    } catch (error) {
      const errorData = handleApiError(error, notification);
      setErrors(errorData.errors || {});
    } finally {
      setLoading(false);
    }
  };

  const handleSchedulePayment = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErrors({});

    try {
      const response = await billPaymentAPI.schedulePayment({
        ...paymentForm,
        amount: parseFloat(paymentForm.amount)
      });
      
      if (response.status === 'success') {
        setPayments(prev => [response.data.payment, ...prev]);
        setShowSchedulePayment(false);
        setPaymentForm({
          payee_id: '',
          from_account_id: '',
          amount: '',
          scheduled_date: new Date().toISOString().split('T')[0],
          memo: '',
          is_recurring: false,
          recurring_frequency: '',
          recurring_end_date: ''
        });
        notification.showSuccess('Payment scheduled successfully!');
      }
    } catch (error) {
      const errorData = handleApiError(error, notification);
      setErrors(errorData.errors || {});
    } finally {
      setLoading(false);
    }
  };

  const handleQuickPay = (payee) => {
    setPaymentForm(prev => ({
      ...prev,
      payee_id: payee.id,
      amount: payee.default_amount || ''
    }));
    setShowSchedulePayment(true);
  };

  const handleCancelPayment = async (paymentId) => {
    try {
      const response = await billPaymentAPI.cancelPayment(paymentId);
      if (response.status === 'success') {
        setPayments(prev => prev.map(payment => 
          payment.id === paymentId ? { ...payment, status: 'cancelled' } : payment
        ));
        notification.showSuccess('Payment cancelled successfully!');
      }
    } catch (error) {
      handleApiError(error, notification);
    }
  };

  const handleDeletePayee = async (payeeId) => {
    if (!window.confirm('Are you sure you want to delete this payee?')) return;

    try {
      const response = await billPaymentAPI.deletePayee(payeeId);
      if (response.status === 'success') {
        setPayees(prev => prev.filter(payee => payee.id !== payeeId));
        notification.showSuccess('Payee deleted successfully!');
      }
    } catch (error) {
      handleApiError(error, notification);
    }
  };

  if (loading && payees.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-600 border-t-transparent mx-auto mb-4"></div>
          <p className="text-gray-600 font-medium">Loading bill payment data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm sticky top-0 z-10">
        <div className="px-4 py-3">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-gradient-to-r from-green-500 to-green-600 rounded-xl flex items-center justify-center">
              <span className="text-white text-xl">💰</span>
            </div>
            <div>
              <h1 className="text-lg font-bold text-gray-900">Bill Pay</h1>
              <p className="text-xs text-gray-500">Manage and pay your bills online</p>
            </div>
          </div>
        </div>
      </div>

      <div className="px-4 py-6">
      {/* Tabs */}
        <div className="flex justify-center mb-6">
        <div className="bg-gray-100 p-1 rounded-lg">
          <button
            onClick={() => setActiveTab('pay-bills')}
              className={`px-4 py-2 rounded-md font-medium transition-colors text-sm ${
              activeTab === 'pay-bills'
                  ? 'bg-white text-green-600 shadow-sm'
                : 'text-gray-600 hover:text-gray-800'
            }`}
          >
            Pay Bills
          </button>
          <button
            onClick={() => setActiveTab('manage-payees')}
              className={`px-4 py-2 rounded-md font-medium transition-colors text-sm ${
              activeTab === 'manage-payees'
                  ? 'bg-white text-green-600 shadow-sm'
                : 'text-gray-600 hover:text-gray-800'
            }`}
          >
            Manage Payees
          </button>
          <button
            onClick={() => setActiveTab('payment-history')}
              className={`px-4 py-2 rounded-md font-medium transition-colors text-sm ${
              activeTab === 'payment-history'
                  ? 'bg-white text-green-600 shadow-sm'
                : 'text-gray-600 hover:text-gray-800'
            }`}
          >
              History
          </button>
        </div>
      </div>

      {/* Pay Bills Tab */}
      {activeTab === 'pay-bills' && (
        <div className="space-y-6">
          {/* Quick Pay Section */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-semibold text-gray-900">Quick Pay</h2>
                <button
                  onClick={() => setShowSchedulePayment(true)}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg text-sm hover:bg-green-700 transition-colors"
                >
                  Schedule Payment
                </button>
              </div>
              
              {payees.length === 0 ? (
                <div className="text-center py-8">
                  <p className="text-gray-500 mb-4">No payees found. Add a payee to get started.</p>
                  <button
                    onClick={() => {
                      setActiveTab('manage-payees');
                      setShowAddPayee(true);
                    }}
                    className="px-4 py-2 bg-green-600 text-white rounded-lg text-sm hover:bg-green-700 transition-colors"
                  >
                    Add Your First Payee
                  </button>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {payees.slice(0, 6).map((payee) => (
                    <div key={payee.id} className="border border-gray-200 rounded-xl p-4 hover:shadow-md transition-shadow">
                      <div className="flex items-center justify-between mb-3">
                        <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center text-lg">
                          {getCategoryIcon(payee.category)}
              </div>
                        {payee.autopay_enabled && (
                          <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-medium">
                            AutoPay
                          </span>
                        )}
              </div>
                      <h3 className="font-semibold text-gray-900 mb-1">{payee.display_name || payee.name}</h3>
                      <p className="text-sm text-gray-500 mb-3">{payee.formatted_account_number}</p>
                      {payee.default_amount && (
                        <p className="text-lg font-semibold text-gray-900 mb-3">
                          {formatCurrencyWithUserCurrency(payee.default_amount)}
                        </p>
                      )}
                      <button
                        onClick={() => handleQuickPay(payee)}
                        className="w-full py-2 bg-green-600 text-white rounded-lg text-sm hover:bg-green-700 transition-colors"
                      >
                        Pay Now
                      </button>
            </div>
                  ))}
          </div>
              )}
            </div>
            
            {/* Upcoming Bills */}
            {payees.length > 0 && (
              <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">Your Payees</h2>
                <div className="space-y-3">
              {payees.map((payee) => (
                    <div key={payee.id} className="flex items-center justify-between p-4 border border-gray-200 rounded-xl">
                  <div className="flex items-center space-x-4">
                        <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center text-xl">
                      {getCategoryIcon(payee.category)}
                    </div>
                    <div>
                          <h3 className="font-semibold text-gray-900">{payee.display_name || payee.name}</h3>
                          <p className="text-sm text-gray-500">{categories[payee.category]} • {payee.formatted_account_number}</p>
                          {payee.next_payment && (
                            <p className="text-xs text-blue-600">Next: {payee.next_payment.date} • {payee.next_payment.amount}</p>
                          )}
                    </div>
                  </div>
                      <div className="flex items-center space-x-2">
                        {payee.autopay_enabled && (
                          <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-medium">
                          AutoPay
                        </span>
                      )}
                        <button
                          onClick={() => handleQuickPay(payee)}
                          className="px-3 py-1 bg-green-600 text-white rounded text-sm hover:bg-green-700 transition-colors"
                        >
                          Pay
                      </button>
                    </div>
                  </div>
                  ))}
                </div>
            </div>
            )}
        </div>
      )}

      {/* Manage Payees Tab */}
      {activeTab === 'manage-payees' && (
        <div className="space-y-6">
          <div className="flex justify-between items-center">
              <h2 className="text-xl font-semibold text-gray-900">Manage Payees</h2>
              <button
                onClick={() => setShowAddPayee(true)}
                className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
              >
                <span className="text-lg">+</span>
                <span>Add Payee</span>
              </button>
          </div>

            {payees.length === 0 ? (
              <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8 text-center">
                <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl">💳</span>
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">No Payees Yet</h3>
                <p className="text-gray-500 mb-4">Add payees to start paying bills online</p>
                <button
                  onClick={() => setShowAddPayee(true)}
                  className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                >
                  Add Your First Payee
                </button>
              </div>
            ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {payees.map((payee) => (
                  <div key={payee.id} className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
                <div className="flex items-center justify-between mb-4">
                      <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center text-xl">
                    {getCategoryIcon(payee.category)}
                  </div>
                      <span className="px-2 py-1 bg-green-100 text-green-800 rounded-full text-xs font-medium">
                        Active
                  </span>
                </div>
                
                    <h3 className="font-semibold text-gray-900 mb-1">{payee.display_name || payee.name}</h3>
                    <p className="text-sm text-gray-500 mb-2">{categories[payee.category]}</p>
                    <p className="text-sm text-gray-500 mb-4">Account: {payee.formatted_account_number}</p>
                    
                    {payee.default_amount && (
                      <p className="text-lg font-semibold text-gray-900 mb-3">
                        Default: {formatCurrencyWithUserCurrency(payee.default_amount)}
                      </p>
                    )}
                
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <p className="text-xs text-gray-500">AutoPay</p>
                        <p className="text-sm font-medium text-gray-900">
                          {payee.autopay_enabled ? 'Enabled' : 'Disabled'}
                        </p>
                      </div>
                </div>
                
                <div className="flex space-x-2">
                      <button
                        onClick={() => handleQuickPay(payee)}
                        className="flex-1 py-2 bg-green-600 text-white rounded-lg text-sm hover:bg-green-700 transition-colors"
                      >
                    Pay
                  </button>
                      <button
                        onClick={() => handleDeletePayee(payee.id)}
                        className="px-3 py-2 text-red-600 border border-red-600 rounded-lg text-sm hover:bg-red-50 transition-colors"
                      >
                        Delete
                      </button>
                </div>
              </div>
            ))}
          </div>
            )}
        </div>
      )}

      {/* Payment History Tab */}
      {activeTab === 'payment-history' && (
        <div className="space-y-6">
          <div className="flex justify-between items-center">
              <h2 className="text-xl font-semibold text-gray-900">Complete Transaction History</h2>
              <div className="flex space-x-2">
                <select className="px-3 py-2 border border-gray-300 rounded-lg text-sm">
                  <option value="all">All Transactions</option>
                  <option value="bill_payments">Bill Payments Only</option>
                  <option value="transfers">Transfers Only</option>
                  <option value="transactions">Bank Transactions</option>
              </select>
              </div>
            </div>

            {comprehensiveHistory.length === 0 ? (
              <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8 text-center">
                <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl">📋</span>
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">No Transaction History</h3>
                <p className="text-gray-500">Your complete transaction history will appear here</p>
          </div>
            ) : (
              <>
                {/* Desktop Table View */}
                <div className="hidden md:block bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                          <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Transaction
                          </th>
                          <th className="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Type
                    </th>
                          <th className="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Amount
                    </th>
                          <th className="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Date
                    </th>
                          <th className="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                          <th className="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Account
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                        {comprehensiveHistory.map((transaction) => (
                          <tr key={`${transaction.source_type}-${transaction.id}`} className="hover:bg-gray-50">
                            <td className="px-3 py-3">
                              <div className="flex items-center space-x-2 min-w-0">
                                <div className="w-6 h-6 bg-green-100 rounded-md flex items-center justify-center flex-shrink-0 text-sm">
                                  {transaction.icon || (transaction.source_type === 'transfer' ? '🔄' : 
                                  transaction.source_type.includes('bill_payment') ? '💳' : '💰')}
                                </div>
                                <div className="min-w-0 flex-1">
                                  <div className="font-medium text-gray-900 text-sm truncate max-w-[200px]" title={transaction.description}>
                                    {transaction.description}
                                  </div>
                                  <div className="text-xs text-gray-500 truncate max-w-[200px]" title={transaction.category}>
                                    {transaction.category}
                                  </div>
                                </div>
                              </div>
                            </td>
                            <td className="px-2 py-3">
                              <span className={`inline-flex px-1.5 py-0.5 text-xs font-medium rounded whitespace-nowrap ${
                                transaction.source_type === 'transaction' ? 'bg-blue-100 text-blue-700' :
                                transaction.source_type === 'transfer' ? 'bg-purple-100 text-purple-700' :
                                transaction.source_type.includes('bill_payment') ? 'bg-orange-100 text-orange-700' :
                                'bg-gray-100 text-gray-700'
                              }`}>
                                {transaction.source_type === 'transaction' ? 'Bank' :
                                 transaction.source_type === 'transfer' ? 'Transfer' :
                                 transaction.source_type.includes('bill_payment') ? 'Bill' :
                                 'Other'}
                              </span>
                      </td>
                            <td className="px-2 py-3">
                              <div className={`font-semibold text-sm whitespace-nowrap ${
                                (transaction.amount || 0) >= 0 ? 'text-green-600' : 'text-red-600'
                              }`}>
                                {(transaction.amount || 0) >= 0 ? '+' : ''}${Math.abs(transaction.amount || 0).toFixed(2)}
                              </div>
                      </td>
                            <td className="px-2 py-3 text-xs text-gray-500 whitespace-nowrap">
                              {new Date(transaction.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                      </td>
                            <td className="px-2 py-3">
                              <span className={`inline-flex px-1.5 py-0.5 text-xs font-medium rounded whitespace-nowrap ${
                                transaction.status === 'completed' ? 'bg-green-100 text-green-700' :
                                transaction.status === 'pending' ? 'bg-yellow-100 text-yellow-700' :
                                transaction.status === 'processing' ? 'bg-blue-100 text-blue-700' :
                                transaction.status === 'scheduled' ? 'bg-purple-100 text-purple-700' :
                                'bg-red-100 text-red-700'
                              }`}>
                                {transaction.status}
                        </span>
                      </td>
                            <td className="px-2 py-3 text-xs text-gray-500">
                              <div className="truncate max-w-[80px]" title={transaction.account_name || 'N/A'}>
                                {transaction.account_name || 'N/A'}
                              </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
                  </div>
                </div>

                {/* Mobile Card View */}
                <div className="md:hidden space-y-3">
                  {comprehensiveHistory.map((transaction) => (
                    <div key={`${transaction.source_type}-${transaction.id}`} className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
                      <div className="flex items-start space-x-3">
                        <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
                          {transaction.icon || (transaction.source_type === 'transfer' ? '🔄' : 
                          transaction.source_type.includes('bill_payment') ? '💳' : '💰')}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between">
                            <div className="min-w-0 flex-1">
                              <p className="font-medium text-gray-900 text-sm truncate">{transaction.description}</p>
                              <p className="text-xs text-gray-500 truncate">{transaction.category}</p>
                            </div>
                            <div className="text-right flex-shrink-0 ml-4">
                              <p className={`font-semibold text-sm ${
                                (transaction.amount || 0) >= 0 ? 'text-green-600' : 'text-red-600'
                              }`}>
                                {(transaction.amount || 0) >= 0 ? '+' : ''}${Math.abs(transaction.amount || 0).toFixed(2)}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center justify-between mt-2">
                            <div className="flex items-center space-x-2">
                              <span className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full ${
                                transaction.source_type === 'transaction' ? 'bg-blue-100 text-blue-700' :
                                transaction.source_type === 'transfer' ? 'bg-purple-100 text-purple-700' :
                                transaction.source_type.includes('bill_payment') ? 'bg-orange-100 text-orange-700' :
                                'bg-gray-100 text-gray-700'
                              }`}>
                                {transaction.source_type === 'transaction' ? 'Bank' :
                                 transaction.source_type === 'transfer' ? 'Transfer' :
                                 transaction.source_type.includes('bill_payment') ? 'Bill' :
                                 'Other'}
                              </span>
                              <span className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full ${
                                transaction.status === 'completed' ? 'bg-green-100 text-green-700' :
                                transaction.status === 'pending' ? 'bg-yellow-100 text-yellow-700' :
                                transaction.status === 'processing' ? 'bg-blue-100 text-blue-700' :
                                transaction.status === 'scheduled' ? 'bg-purple-100 text-purple-700' :
                                'bg-red-100 text-red-700'
                              }`}>
                                {transaction.status}
                              </span>
                            </div>
                            <span className="text-xs text-gray-500">
                              {new Date(transaction.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        )}
      </div>

      {/* Add Payee Modal */}
      {showAddPayee && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-2xl w-full max-h-screen overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-xl font-bold text-gray-900">Add New Payee</h3>
                <button 
                  onClick={() => setShowAddPayee(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <span className="text-2xl">&times;</span>
                </button>
              </div>

              <form onSubmit={handleAddPayee} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Company Name *</label>
                    <input
                      type="text"
                      name="name"
                      value={payeeForm.name}
                      onChange={handlePayeeFormChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Display Name</label>
                    <input
                      type="text"
                      name="display_name"
                      value={payeeForm.display_name}
                      onChange={handlePayeeFormChange}
                      placeholder="Optional nickname"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Category *</label>
                    <select
                      name="category"
                      value={payeeForm.category}
                      onChange={handlePayeeFormChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                      required
                    >
                      <option value="">Select category</option>
                      {Object.entries(categories).map(([key, value]) => (
                        <option key={key} value={key}>{value}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Account Number *</label>
                    <input
                      type="text"
                      name="account_number"
                      value={payeeForm.account_number}
                      onChange={handlePayeeFormChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                      required
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Default Amount</label>
                  <input
                    type="number"
                    name="default_amount"
                    value={payeeForm.default_amount}
                    onChange={handlePayeeFormChange}
                    step="0.01"
                    placeholder="0.00"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  />
                </div>

                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    name="autopay_enabled"
                    id="autopay_enabled"
                    checked={payeeForm.autopay_enabled}
                    onChange={handlePayeeFormChange}
                    className="w-4 h-4 text-green-600 bg-gray-100 border-gray-300 rounded focus:ring-green-500"
                  />
                  <label htmlFor="autopay_enabled" className="text-sm font-medium text-gray-700">
                    Enable AutoPay
                  </label>
                </div>

                {payeeForm.autopay_enabled && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">AutoPay Day</label>
                      <input
                        type="number"
                        name="autopay_day"
                        value={payeeForm.autopay_day}
                        onChange={handlePayeeFormChange}
                        min="1"
                        max="31"
                        placeholder="Day of month"
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">AutoPay Amount</label>
                      <input
                        type="number"
                        name="autopay_amount"
                        value={payeeForm.autopay_amount}
                        onChange={handlePayeeFormChange}
                        step="0.01"
                        placeholder="0.00"
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                      />
                    </div>
                  </div>
                )}

                <div className="flex space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowAddPayee(false)}
                    className="flex-1 py-3 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={loading}
                    className="flex-1 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
                  >
                    {loading ? 'Adding...' : 'Add Payee'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Schedule Payment Modal */}
      {showSchedulePayment && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-lg w-full">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-xl font-bold text-gray-900">Schedule Payment</h3>
                <button 
                  onClick={() => setShowSchedulePayment(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <span className="text-2xl">&times;</span>
                </button>
              </div>

              <form onSubmit={handleSchedulePayment} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Select Payee *</label>
                  <select
                    name="payee_id"
                    value={paymentForm.payee_id}
                    onChange={handlePaymentFormChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                    required
                  >
                    <option value="">Choose a payee</option>
                    {payees.map(payee => (
                      <option key={payee.id} value={payee.id}>
                        {payee.display_name || payee.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">From Account *</label>
                  <select
                    name="from_account_id"
                    value={paymentForm.from_account_id}
                    onChange={handlePaymentFormChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                    required
                  >
                    <option value="">Select account</option>
                    {accounts.map(account => (
                      <option key={account.id} value={account.id}>
                        {account.account_name} • {formatCurrencyWithUserCurrency(account.balance)}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Amount *</label>
                    <input
                      type="number"
                      name="amount"
                      value={paymentForm.amount}
                      onChange={handlePaymentFormChange}
                      step="0.01"
                      min="0.01"
                      placeholder="0.00"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Payment Date *</label>
                    <input
                      type="date"
                      name="scheduled_date"
                      value={paymentForm.scheduled_date}
                      onChange={handlePaymentFormChange}
                      min={new Date().toISOString().split('T')[0]}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                      required
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Memo</label>
                  <input
                    type="text"
                    name="memo"
                    value={paymentForm.memo}
                    onChange={handlePaymentFormChange}
                    placeholder="Optional note"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  />
                </div>

                <div className="flex space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowSchedulePayment(false)}
                    className="flex-1 py-3 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={loading}
                    className="flex-1 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
                  >
                    {loading ? 'Scheduling...' : 'Schedule Payment'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BillPay; 